import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getProjectDetailsReport } from "reduxStore/action/webReportActions";
import { getReportPropertyList } from "reduxStore/action/reportActions";
import TableDataNotFound from "component/TableDataNotFound";
import strings from "translate/language";
import Breadcrumb from "component/common/breadcrumb";
import Pagination from "component/common/Pagination";
import { handleListResponse, seo } from "utils/utils";
import Select from "react-select";
import moment from "moment";
import * as XLSX from "xlsx"; // Importing the xlsx library
import ExcelJS from "exceljs";
import { Slack } from "react-feather";

const ProjectDetailsReportPage = ({ isAuth = false }) => {
  const dispatch = useDispatch();
  const [form, setForm] = useState(() => {
    // Get initial state from local storage or set to default
    const savedForm = localStorage.getItem("projectReportForm");
    return savedForm
      ? JSON.parse(savedForm)
      : { on_date: new Date(), property_title: "" };
  });

  useEffect(() => {
    dispatch(getProjectDetailsReport({}));
    dispatch(getReportPropertyList());
  }, []);

  const [isSubmitted, setIsSubmitted] = useState(() => {
    // Get initial state from local storage
    return localStorage.getItem("isSubmitted") === "true";
  });

  useEffect(() => {
    // Save form data to local storage whenever it changes
    localStorage.setItem("projectReportForm", JSON.stringify(form));
  }, [form]);

  useEffect(() => {
    // Save isSubmitted state to local storage
    localStorage.setItem("isSubmitted", isSubmitted);
  }, [isSubmitted]);

  const [propertyList, setPropertyList] = useState([]);

  const ReportListSelector = useSelector((state) => state.report);

  useEffect(() => {
    const { reportPropertyList } = ReportListSelector;
    if (reportPropertyList) {
      if (reportPropertyList?.status === 200) {
        setPropertyList(handleListResponse(reportPropertyList.data));
      } else {
        setPropertyList([]);
      }
    }
  }, [ReportListSelector]);

  const [formValid, setFormValid] = useState({ on_date: "" });
  const { projectDetails } = useSelector((state) => state.webreport);

  useEffect(() => {
    seo({
      title: "Report Project wise",
      metaDescription: "All Project",
      canonicalLink: window.location.href,
    });
    let interval = setInterval(() => {}, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const bookings = projectDetails?.data?.bookings || {};
  const siteVisits = projectDetails?.data?.siteVisits || {};

  const bookings1 = projectDetails?.data?.bookings1 || [];
  const siteVisits1 = projectDetails?.data?.siteVisits1 || [];

  useEffect(() => {
    // console.log("Project Report State:", projectDetails);
  }, [projectDetails]);

  const fetchProjectReport = () => {
    if (validateForm()) {
      setFormValid({ on_date: "" });
      setIsSubmitted(true);
      dispatch(getProjectDetailsReport(form));
    }
  };

  const handleInputField = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (selectedOption) => {
    setForm({ ...form, property_title: selectedOption });
  };

  const validateForm = () => {
    if (!form.on_date) {
      setFormValid({ on_date: "Please fill in the date" });
      return false;
    }
    return true;
  };

  const filterDetail = () => {
    fetchProjectReport();
  };

  const filterReset = () => {
    setForm({ on_date: new Date(), property_title: "" });
    setIsSubmitted(false);
    dispatch(getProjectDetailsReport({}));
    // Clear local storage on reset
    localStorage.removeItem("projectReportForm");
    localStorage.removeItem("isSubmitted");
  };

  const formatDate = (dateString) => {
    if (!dateString) return ""; // Return empty if no date

    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0"); // Get day and pad with zero if needed
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Get month (0-indexed) and pad with zero
    const year = String(date.getFullYear()).slice(-2); // Get last two digits of year

    return `${day}/${month}/${year}`; // Return formatted date
  };
  // Ensure you have ExcelJS imported

  const exportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Justoverse Project Wise Report");

    const data = [];
    data.push({ PropertyTitle: "", MTD: "", FTD: "", YTD: "" });
    data.push({ PropertyTitle: "", MTD: "", FTD: "", YTD: "" });
    data.push({ PropertyTitle: "", MTD: "MTD", FTD: "FTD", YTD: "YTD" });

    if (form.property_title && isSubmitted) {
      data.push({ PropertyTitle: "", MTD: "", FTD: "", YTD: "" });

      // Collect booking data for this specific property
      const ftdBookingData = bookings1[0]?.ftd.find(
        (b) => b.property_title === form.property_title.value
      ) || { GrossBookings: 0, Cancellation: 0, NetBookings: 0, Token: 0 };
      const mtdBookingData = bookings1[0]?.mtd.find(
        (b) => b.property_title === form.property_title.value
      ) || { GrossBookings: 0, Cancellation: 0, NetBookings: 0, Token: 0 };
      const ytdBookingData = bookings1[0]?.ytd.find(
        (b) => b.property_title === form.property_title.value
      ) || { GrossBookings: 0, Cancellation: 0, NetBookings: 0, Token: 0 };

      // Collect site visit data for this specific property
      const ftdSiteVisitData = siteVisits1[0]?.ftd.find(
        (s) => s.property_title === form.property_title.value
      ) || {
        SiteVisits: 0,
        Revisits: 0,
        DirectWalkInVisits: 0,
        ReferralPartnerVisits: 0,
        ReferenceVisits: 0,
        ChannelPartnerVisits: 0,
        PreSalesVisits: 0,
        ExhibitionVisits: 0,
        ChannelPartnerRevisitsCount: 0,
        DirectWalkInRevisitsCount: 0,
        ReferenceRevisitsCount: 0,
        ReferralPartnerRevisitsCount: 0,
        PreSalesRevisitsCount: 0,
        ExhibitionRevisitsCount: 0,
        OtherVisitsCount: 0,
        OtherRevisitsCount: 0,
      };
      const mtdSiteVisitData = siteVisits1[0]?.mtd.find(
        (s) => s.property_title === form.property_title.value
      ) || {
        SiteVisits: 0,
        Revisits: 0,
        DirectWalkInVisits: 0,
        ReferralPartnerVisits: 0,
        ReferenceVisits: 0,
        ChannelPartnerVisits: 0,
        PreSalesVisits: 0,
        ExhibitionVisits: 0,
        ChannelPartnerRevisitsCount: 0,
        DirectWalkInRevisitsCount: 0,
        ReferenceRevisitsCount: 0,
        ReferralPartnerRevisitsCount: 0,
        PreSalesRevisitsCount: 0,
        ExhibitionRevisitsCount: 0,
        OtherVisitsCount: 0,
        OtherRevisitsCount: 0,
      };
      const ytdSiteVisitData = siteVisits1[0]?.ytd.find(
        (s) => s.property_title === form.property_title.value
      ) || {
        SiteVisits: 0,
        Revisits: 0,
        DirectWalkInVisits: 0,
        ReferralPartnerVisits: 0,
        ReferenceVisits: 0,
        ChannelPartnerVisits: 0,
        PreSalesVisits: 0,
        ExhibitionVisits: 0,
        ChannelPartnerRevisitsCount: 0,
        DirectWalkInRevisitsCount: 0,
        ReferenceRevisitsCount: 0,
        ReferralPartnerRevisitsCount: 0,
        PreSalesRevisitsCount: 0,
        ExhibitionRevisitsCount: 0,
        OtherVisitsCount: 0,
        OtherRevisitsCount: 0,
      };

      // Add property header
      data.push({
        PropertyTitle: `Property Title: ${form.property_title.value}`,
        MTD: "",
        FTD: "",
        YTD: "",
      });
      // data.push({ PropertyTitle: "", YTD: "", MTD: "", FTD: "" });
      // Bookings Section
      data.push({ PropertyTitle: "Bookings", MTD: "", FTD: "", YTD: "" });
      data.push({
        PropertyTitle: "Gross Bookings",
        MTD: mtdBookingData.GrossBookings,
        FTD: ftdBookingData.GrossBookings,
        YTD: ytdBookingData.GrossBookings,
      });
      data.push({
        PropertyTitle: "Cancellations",
        MTD: mtdBookingData.Cancellation,
        FTD: ftdBookingData.Cancellation,
        YTD: ytdBookingData.Cancellation,
      });
      data.push({
        PropertyTitle: "Net Bookings",
        MTD: mtdBookingData.NetBookings,
        FTD: ftdBookingData.NetBookings,
        YTD: ytdBookingData.NetBookings,
      });
      data.push({ PropertyTitle: "", MTD: "", FTD: "", YTD: "" });
      // Site Visits Section
      data.push({ PropertyTitle: "Site Visits", MTD: "", FTD: "", YTD: "" });
      data.push({
        PropertyTitle: "Total Site Visits",
        MTD: mtdSiteVisitData.SiteVisits,
        FTD: ftdSiteVisitData.SiteVisits,
        YTD: ytdSiteVisitData.SiteVisits,
      });
      data.push({
        PropertyTitle: "Channel Partner Visits",
        MTD: mtdSiteVisitData.ChannelPartnerVisits,
        FTD: ftdSiteVisitData.ChannelPartnerVisits,
        YTD: ytdSiteVisitData.ChannelPartnerVisits,
      });
      data.push({
        PropertyTitle: "Direct Walk-In Visits",
        MTD: mtdSiteVisitData.DirectWalkInVisits,
        FTD: ftdSiteVisitData.DirectWalkInVisits,
        YTD: ytdSiteVisitData.DirectWalkInVisits,
      });
      data.push({
        PropertyTitle: "Referral Partner Visits",
        MTD: mtdSiteVisitData.ReferralPartnerVisits,
        FTD: ftdSiteVisitData.ReferralPartnerVisits,
        YTD: ytdSiteVisitData.ReferralPartnerVisits,
      });
      data.push({
        PropertyTitle: "Reference Visits",
        MTD: mtdSiteVisitData.ReferenceVisits,
        FTD: ftdSiteVisitData.ReferenceVisits,
        YTD: ytdSiteVisitData.ReferenceVisits,
      });
      data.push({
        PropertyTitle: "Pre-Sales Visits",
        MTD: mtdSiteVisitData.PreSalesVisits,
        FTD: ftdSiteVisitData.PreSalesVisits,
        YTD: ytdSiteVisitData.PreSalesVisits,
      });
      data.push({
        PropertyTitle: "Exhibition Visits",
        MTD: mtdSiteVisitData.ExhibitionVisits,
        FTD: ftdSiteVisitData.ExhibitionVisits,
        YTD: ytdSiteVisitData.ExhibitionVisits,
      });
      data.push({
        PropertyTitle: "Other Visits",
        MTD: mtdSiteVisitData.OtherVisitsCount,
        FTD: ftdSiteVisitData.OtherVisitsCount,
        YTD: ytdSiteVisitData.OtherVisitsCount,
      });

      data.push({ PropertyTitle: "Site Revisits", MTD: "", FTD: "", YTD: "" });
      data.push({
        PropertyTitle: "Revisits",
        MTD: mtdSiteVisitData.Revisits || 0,
        FTD: ftdSiteVisitData.Revisits || 0,
        YTD: ytdSiteVisitData.Revisits || 0,
      });
      data.push({
        PropertyTitle: "Channel Partner Revisits",
        MTD: mtdSiteVisitData.ChannelPartnerRevisitsCount,
        FTD: ftdSiteVisitData.ChannelPartnerRevisitsCount,
        YTD: ytdSiteVisitData.ChannelPartnerRevisitsCount || 0,
      });
      data.push({
        PropertyTitle: "Direct Walk-In Revisits",
        MTD: mtdSiteVisitData.DirectWalkInRevisitsCount,
        FTD: ftdSiteVisitData.DirectWalkInRevisitsCount,
        YTD: ytdSiteVisitData.DirectWalkInRevisitsCount || 0,
      });
      data.push({
        PropertyTitle: "Referral Partner Revisits",
        MTD: mtdSiteVisitData.ReferralPartnerRevisitsCount,
        FTD: ftdSiteVisitData.ReferralPartnerRevisitsCount,
        YTD: ytdSiteVisitData.ReferralPartnerRevisitsCount || 0,
      });
      data.push({
        PropertyTitle: "Reference Revisits",
        MTD: mtdSiteVisitData.ReferenceRevisitsCount,
        FTD: ftdSiteVisitData.ReferenceRevisitsCount,
        YTD: ytdSiteVisitData.ReferenceRevisitsCount || 0,
      });
      data.push({
        PropertyTitle: "Pre-Sales Revisits",
        MTD: mtdSiteVisitData.PreSalesRevisitsCount,
        FTD: ftdSiteVisitData.PreSalesRevisitsCount,
        YTD: ytdSiteVisitData.PreSalesRevisitsCount || 0,
      });
      data.push({
        PropertyTitle: "Exhibition Revisits",
        MTD: mtdSiteVisitData.ExhibitionRevisitsCount,
        FTD: ftdSiteVisitData.ExhibitionRevisitsCount,
        YTD: ytdSiteVisitData.ExhibitionRevisitsCount || 0,
      });
      data.push({
        PropertyTitle: "Other Revisits",
        MTD: mtdSiteVisitData.OtherRevisitsCount,
        FTD: ftdSiteVisitData.OtherRevisitsCount,
        YTD: ytdSiteVisitData.OtherRevisitsCount || 0,
      });
    } else {
      data.push({ PropertyTitle: "", MTD: "", FTD: "", YTD: "" });
      // Add a heading for all properties
      data.push({ PropertyTitle: "All Properties", MTD: "", FTD: "", YTD: "" });
      data.push({ PropertyTitle: "Bookings", MTD: "", FTD: "", YTD: "" });
      // Overall bookings data
      data.push({
        PropertyTitle: "Gross Bookings",
        YTD: bookings.ytd?.GrossBookings || 0,
        MTD: bookings.mtd?.GrossBookings || 0,
        FTD: bookings.ftd?.GrossBookings || 0,
      });
      data.push({
        PropertyTitle: "Cancellations",
        YTD: bookings.ytd?.Cancellation || 0,
        MTD: bookings.mtd?.Cancellation || 0,
        FTD: bookings.ftd?.Cancellation || 0,
      });
      data.push({
        PropertyTitle: "Net Bookings",
        YTD: bookings.ytd?.NetBookings || 0,
        MTD: bookings.mtd?.NetBookings || 0,
        FTD: bookings.ftd?.NetBookings || 0,
      });
      // Overall site visits data
      data.push({ PropertyTitle: "Site Visits", MTD: "", FTD: "", YTD: "" });
      data.push({
        PropertyTitle: "Total Site Visits",
        MTD: siteVisits.mtd?.SiteVisits || 0,
        FTD: siteVisits.ftd?.SiteVisits || 0,
        YTD: siteVisits.ytd?.SiteVisits || 0,
      });
      data.push({
        PropertyTitle: "Channel Partner Visits",
        MTD: siteVisits.mtd?.ChannelPartnerVisits || 0,
        FTD: siteVisits.ftd?.ChannelPartnerVisits || 0,
        YTD: siteVisits.ytd?.ChannelPartnerVisits || 0,
      });
      data.push({
        PropertyTitle: "Direct Walk-In Visits",
        MTD: siteVisits.mtd?.DirectWalkInVisits || 0,
        FTD: siteVisits.ftd?.DirectWalkInVisits || 0,
        YTD: siteVisits.ytd?.DirectWalkInVisits || 0,
      });
      data.push({
        PropertyTitle: "Referral Partner Visits",
        MTD: siteVisits.mtd?.ReferralPartnerVisits || 0,
        FTD: siteVisits.ftd?.ReferralPartnerVisits || 0,
        YTD: siteVisits.ytd?.ReferralPartnerVisits || 0,
      });
      data.push({
        PropertyTitle: "Reference Visits",
        MTD: siteVisits.mtd?.ReferenceVisits || 0,
        FTD: siteVisits.ftd?.ReferenceVisits || 0,
        YTD: siteVisits.ytd?.ReferenceVisits || 0,
      });
      data.push({
        PropertyTitle: "Pre-Sales Visits",
        MTD: siteVisits.mtd?.PreSalesVisits || 0,
        FTD: siteVisits.ftd?.PreSalesVisits || 0,
        YTD: siteVisits.ytd?.PreSalesVisits || 0,
      });
      data.push({
        PropertyTitle: "Exhibition Visits",
        MTD: siteVisits.mtd?.ExhibitionVisits || 0,
        FTD: siteVisits.ftd?.ExhibitionVisits || 0,
        YTD: siteVisits.ytd?.ExhibitionVisits || 0,
      });
      data.push({
        PropertyTitle: "Other Visits",
        MTD: siteVisits.mtd?.OtherVisits || 0,
        FTD: siteVisits.ftd?.OtherVisits || 0,
        YTD: siteVisits.ytd?.OtherVisits || 0,
      });

      data.push({ PropertyTitle: "Site Revisits", MTD: "", FTD: "", YTD: "" });
      data.push({
        PropertyTitle: "Revisits",
        MTD: siteVisits.mtd?.Revisits || 0,
        FTD: siteVisits.ftd?.Revisits || 0,
        YTD: siteVisits.ytd?.Revisits || 0,
      });
      data.push({
        PropertyTitle: "Channel Partner Revisits",
        MTD: siteVisits.mtd?.ChannelPartnerRevisits || 0,
        FTD: siteVisits.ftd?.ChannelPartnerRevisits || 0,
        YTD: siteVisits.ytd?.ChannelPartnerRevisits || 0,
      });
      data.push({
        PropertyTitle: "Direct Walk-In Revisits",
        MTD: siteVisits.mtd?.DirectWalkInRevisits || 0,
        FTD: siteVisits.ftd?.DirectWalkInRevisits || 0,
        YTD: siteVisits.ytd?.DirectWalkInRevisits || 0,
      });
      data.push({
        PropertyTitle: "Referral Partner Revisits",
        MTD: siteVisits.mtd?.ReferralPartnerRevisits || 0,
        FTD: siteVisits.ftd?.ReferralPartnerRevisits || 0,
        YTD: siteVisits.ytd?.ReferralPartnerRevisits || 0,
      });
      data.push({
        PropertyTitle: "Reference Revisits",
        MTD: siteVisits.mtd?.ReferenceRevisits || 0,
        FTD: siteVisits.ftd?.ReferenceRevisits || 0,
        YTD: siteVisits.ytd?.ReferenceRevisits || 0,
      });
      data.push({
        PropertyTitle: "Pre-Sales Revisits",
        MTD: siteVisits.mtd?.PreSalesRevisits || 0,
        FTD: siteVisits.ftd?.PreSalesRevisits || 0,
        YTD: siteVisits.ytd?.PreSalesRevisits || 0,
      });
      data.push({
        PropertyTitle: "Exhibition Revisits",
        MTD: siteVisits.mtd?.ExhibitionRevisits || 0,
        FTD: siteVisits.ftd?.ExhibitionRevisits || 0,
        YTD: siteVisits.ytd?.ExhibitionRevisits || 0,
      });
      data.push({
        PropertyTitle: "Other Revisits",
        MTD: siteVisits.mtd?.OtherRevisits || 0,
        FTD: siteVisits.ftd?.OtherRevisits || 0,
        YTD: siteVisits.ytd?.OtherRevisits || 0,
      });
      data.push({ PropertyTitle: "", MTD: "", FTD: "", YTD: "" });
      // Add a section header for individual properties
      // data.push({ PropertyTitle: "Individual Property Data", YTD: "", MTD: "", FTD: "" });
      data.push({ PropertyTitle: "", MTD: "", FTD: "", YTD: "" });
      // Prepare the data structure for each property
      propertyList.forEach((property, index) => {
        // Collect booking data for this property
        const ftdBookingData = bookings1[0]?.ftd.find(
          (b) => b.property_title === property.property_title
        ) || { GrossBookings: 0, Cancellation: 0, NetBookings: 0, Token: 0 };
        const mtdBookingData = bookings1[0]?.mtd.find(
          (b) => b.property_title === property.property_title
        ) || { GrossBookings: 0, Cancellation: 0, NetBookings: 0, Token: 0 };
        const ytdBookingData = bookings1[0]?.ytd.find(
          (b) => b.property_title === property.property_title
        ) || { GrossBookings: 0, Cancellation: 0, NetBookings: 0, Token: 0 };

        // Collect site visit data for this property
        const ftdSiteVisitData = siteVisits1[0]?.ftd.find(
          (s) => s.property_title === property.property_title
        ) || {
          SiteVisits: 0,
          Revisits: 0,
          DirectWalkInVisits: 0,
          ReferralPartnerVisits: 0,
          ReferenceVisits: 0,
          ChannelPartnerVisits: 0,
          PreSalesVisits: 0,
          ExhibitionVisits: 0,
          ChannelPartnerRevisitsCount: 0,
          DirectWalkInRevisitsCount: 0,
          ReferenceRevisitsCount: 0,
          ReferralPartnerRevisitsCount: 0,
          PreSalesRevisitsCount: 0,
          ExhibitionRevisitsCount: 0,
          OtherVisitsCount: 0,
          OtherRevisitsCount: 0,
        };
        const mtdSiteVisitData = siteVisits1[0]?.mtd.find(
          (s) => s.property_title === property.property_title
        ) || {
          SiteVisits: 0,
          Revisits: 0,
          DirectWalkInVisits: 0,
          ReferralPartnerVisits: 0,
          ReferenceVisits: 0,
          ChannelPartnerVisits: 0,
          PreSalesVisits: 0,
          ExhibitionVisits: 0,
          ChannelPartnerRevisitsCount: 0,
          DirectWalkInRevisitsCount: 0,
          ReferenceRevisitsCount: 0,
          ReferralPartnerRevisitsCount: 0,
          PreSalesRevisitsCount: 0,
          ExhibitionRevisitsCount: 0,
          OtherVisitsCount: 0,
          OtherRevisitsCount: 0,
        };
        const ytdSiteVisitData = siteVisits1[0]?.ytd.find(
          (s) => s.property_title === property.property_title
        ) || {
          SiteVisits: 0,
          Revisits: 0,
          DirectWalkInVisits: 0,
          ReferralPartnerVisits: 0,
          ReferenceVisits: 0,
          ChannelPartnerVisits: 0,
          PreSalesVisits: 0,
          ExhibitionVisits: 0,
          ChannelPartnerRevisitsCount: 0,
          DirectWalkInRevisitsCount: 0,
          ReferenceRevisitsCount: 0,
          ReferralPartnerRevisitsCount: 0,
          PreSalesRevisitsCount: 0,
          ExhibitionRevisitsCount: 0,
          OtherVisitsCount: 0,
          OtherRevisitsCount: 0,
        };

        // Add property header
        data.push({
          PropertyTitle: `Property Title: ${property.property_title}`,
          MTD: "",
          FTD: "",
          YTD: "",
        });

        // Bookings Section
        data.push({ PropertyTitle: "Bookings", MTD: "", FTD: "", YTD: "" });
        data.push({
          PropertyTitle: "Gross Bookings",
          MTD: mtdBookingData.GrossBookings,
          FTD: ftdBookingData.GrossBookings,
          YTD: ytdBookingData.GrossBookings,
        });
        data.push({
          PropertyTitle: "Cancellations",
          MTD: mtdBookingData.Cancellation,
          FTD: ftdBookingData.Cancellation,
          YTD: ytdBookingData.Cancellation,
        });
        data.push({
          PropertyTitle: "Net Bookings",
          MTD: mtdBookingData.NetBookings,
          FTD: ftdBookingData.NetBookings,
          YTD: ytdBookingData.NetBookings,
        });

        // Site Visits Section
        data.push({ PropertyTitle: "Site Visits", MTD: "", FTD: "", YTD: "" });
        data.push({
          PropertyTitle: "Total Site Visits",
          MTD: mtdSiteVisitData.SiteVisits,
          FTD: ftdSiteVisitData.SiteVisits,
          YTD: ytdSiteVisitData.SiteVisits,
        });
        data.push({
          PropertyTitle: "Channel Partner Visits",
          MTD: mtdSiteVisitData.ChannelPartnerVisits,
          FTD: ftdSiteVisitData.ChannelPartnerVisits,
          YTD: ytdSiteVisitData.ChannelPartnerVisits,
        });
        data.push({
          PropertyTitle: "Direct Walk-In Visits",
          MTD: mtdSiteVisitData.DirectWalkInVisits,
          FTD: ftdSiteVisitData.DirectWalkInVisits,
          YTD: ytdSiteVisitData.DirectWalkInVisits,
        });
        data.push({
          PropertyTitle: "Referral Partner Visits",
          MTD: mtdSiteVisitData.ReferralPartnerVisits,
          FTD: ftdSiteVisitData.ReferralPartnerVisits,
          YTD: ytdSiteVisitData.ReferralPartnerVisits,
        });
        data.push({
          PropertyTitle: "Reference Visits",
          MTD: mtdSiteVisitData.ReferenceVisits,
          FTD: ftdSiteVisitData.ReferenceVisits,
          YTD: ytdSiteVisitData.ReferenceVisits,
        });
        data.push({
          PropertyTitle: "Pre-Sales Visits",
          MTD: mtdSiteVisitData.PreSalesVisits,
          FTD: ftdSiteVisitData.PreSalesVisits,
          YTD: ytdSiteVisitData.PreSalesVisits,
        });
        data.push({
          PropertyTitle: "Exhibition Visits",
          MTD: mtdSiteVisitData.ExhibitionVisits,
          FTD: ftdSiteVisitData.ExhibitionVisits,
          YTD: ytdSiteVisitData.ExhibitionVisits,
        });
        data.push({
          PropertyTitle: "Other Visits",
          MTD: mtdSiteVisitData.OtherVisitsCount,
          FTD: ftdSiteVisitData.OtherVisitsCount,
          YTD: ytdSiteVisitData.OtherVisitsCount,
        });

        // Revisits Section
        data.push({
          PropertyTitle: "Site Revisits",
          MTD: "",
          FTD: "",
          YTD: "",
        });
        data.push({
          PropertyTitle: "Revisits",
          MTD: mtdSiteVisitData.Revisits,
          FTD: ftdSiteVisitData.Revisits,
          YTD: ytdSiteVisitData.Revisits,
        });
        data.push({
          PropertyTitle: "Channel Partner Revisits",
          MTD: mtdSiteVisitData.ChannelPartnerRevisitsCount,
          FTD: ftdSiteVisitData.ChannelPartnerRevisitsCount,
          YTD: ytdSiteVisitData.ChannelPartnerRevisitsCount,
        });
        data.push({
          PropertyTitle: "Direct Walk-In Revisits",
          MTD: mtdSiteVisitData.DirectWalkInRevisitsCount,
          FTD: ftdSiteVisitData.DirectWalkInRevisitsCount,
          YTD: ytdSiteVisitData.DirectWalkInRevisitsCount,
        });
        data.push({
          PropertyTitle: "Referral Partner Revisits",
          MTD: mtdSiteVisitData.ReferralPartnerRevisitsCount,
          FTD: ftdSiteVisitData.ReferralPartnerRevisitsCount,
          YTD: ytdSiteVisitData.ReferralPartnerRevisitsCount,
        });
        data.push({
          PropertyTitle: "Reference Revisits",
          MTD: mtdSiteVisitData.ReferenceRevisitsCount,
          FTD: ftdSiteVisitData.ReferenceRevisitsCount,
          YTD: ytdSiteVisitData.ReferenceRevisitsCount,
        });
        data.push({
          PropertyTitle: "Pre-Sales Revisits",
          MTD: mtdSiteVisitData.PreSalesRevisitsCount,
          FTD: ftdSiteVisitData.PreSalesRevisitsCount,
          YTD: ytdSiteVisitData.PreSalesRevisitsCount,
        });
        data.push({
          PropertyTitle: "Exhibition Revisits",
          MTD: mtdSiteVisitData.ExhibitionRevisitsCount,
          FTD: ftdSiteVisitData.ExhibitionRevisitsCount,
          YTD: ytdSiteVisitData.ExhibitionRevisitsCount,
        });
        data.push({
          PropertyTitle: "Other Revisits",
          MTD: mtdSiteVisitData.OtherRevisitsCount,
          FTD: ftdSiteVisitData.OtherRevisitsCount,
          YTD: ytdSiteVisitData.OtherRevisitsCount,
        });

        data.push({ PropertyTitle: "", MTD: "", FTD: "", YTD: "" });
      });
    }
    // Convert the data array into a worksheet
    worksheet.columns = [
      { header: "Property Title", key: "PropertyTitle", width: 80 },
      { header: "FTD", key: "FTD", width: 35 },
      { header: "MTD", key: "MTD", width: 35 },
      { header: "YTD", key: "YTD", width: 35 },
    ];

    data.forEach((item) => worksheet.addRow(item));

    // Apply borders to all cells
    worksheet.eachRow((row, rowNumber) => {
      row.eachCell((cell, colNumber) => {
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });
    });

    // Additional styling for specific rows
    worksheet.getRow(1).font = { bold: true, size: 14 }; // Title row styling
    worksheet.getRow(1).alignment = {
      vertical: "middle",
      horizontal: "center",
    };
    worksheet.mergeCells("A1:D1"); // Merge cells for the title row
    worksheet.getCell(
      "A1"
    ).value = `Justoverse Project Wise Report on date ${formatDate(
      form.on_date
    )}`;
    const titleCell = worksheet.getCell("A1"); // Reference to the merged cell
    titleCell.border = {
      // Apply borders
      top: { style: "thick" },
      left: { style: "thick" },
      bottom: { style: "thick" },
      right: { style: "thick" },
    };

    worksheet.getRow(1).height = 40;

    // Header row styling
    worksheet.getRow(3).font = { bold: true };
    worksheet.getRow(3).alignment = {
      vertical: "middle",
      horizontal: "center",
    };

    // Title background color
    worksheet.getCell("A1").fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFDDDDDD" },
    };

    const valuesToStyle = ["Bookings", "Site Visits", "Site Revisits"];

    // Check all cells in the worksheet for specified values and apply bold formatting
    worksheet.eachRow((row) => {
      row.eachCell((cell) => {
        if (valuesToStyle.includes(cell.value)) {
          // Apply styling to matched cells
          cell.font = { bold: true }; // Make font bold and change font color to yellow
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFDDDDDD" }, // Change background color to a blue shade
          };
        }
      });
    });

    const valuesToStyle1 = ["YTD", "FTD", "MTD"];

    // Check all cells in the worksheet for specified values and apply bold formatting
    worksheet.eachRow((row) => {
      row.eachCell((cell) => {
        if (valuesToStyle1.includes(cell.value)) {
          // Apply styling to matched cells
          cell.font = { bold: true }; // Make font bold
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFDDDDDD" }, // Background color to a blue shade
          };
          cell.alignment = { horizontal: "center", vertical: "middle" }; // Center the text

          // Increase the row height
          row.height = 25; // Adjust this value to increase/decrease cell height
        }
      });
    });

    worksheet.eachRow((row) => {
      row.eachCell((cell) => {
        if (cell.value === "Bookings") {
          const cellAbove = worksheet.getCell(
            cell.address.replace(/\d+$/, (match) => parseInt(match) - 1)
          );

          // Apply different styling to the cell above "Bookings"
          cellAbove.font = { bold: true }; // Bold font

          // Change background color
          cellAbove.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFDDDDDD" }, // Change to light grey background
          };

          // Add border to the cell
          cellAbove.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };

          // Set alignment to center horizontally and vertically
          cellAbove.alignment = {
            horizontal: "center", // Horizontally center the content
            vertical: "middle", // Vertically center the content
          };

          // Change cell height to make it larger
          const rowIndex = parseInt(cellAbove.address.match(/\d+/)[0]); // Get row index
          worksheet.getRow(rowIndex).height = 30; // Set row height to 30 (you can adjust this value)

          // Merge the cell above "Bookings" with the next three cells in the same row
          const startColumn = cellAbove.col; // Starting column of the cell above "Bookings"
          const endColumn = startColumn + 3; // End column to merge (next three cells)
          worksheet.mergeCells(rowIndex, startColumn, rowIndex, endColumn); // Merge cells

          // Optionally, set the width of the column
          // worksheet.getColumn(cellAbove.col).width = 25; // Set column width to 25 (if needed)
        }
      });
    });

    // Prepare for download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Justoverse Project Details Report(${formatDate(
      form.on_date
    )}).xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Handle loading state and errors

  return (
    <div className="page-body">
      {isAuth && (
        <Breadcrumb
          title={strings.projectDetailsReport}
          parentListing={null}
          titleactive={strings.projectDetailsReport}
        />
      )}
      <div className="container-fluid">
        <div className="row">
          <div className="col-sm-12 col-xl-12">
            <div className="card">
              <div className="card-body">
                <form className="theme-form">
                  <div className="row">
                    <div className="col-md-4">
                      <span className="cardText">
                        {strings.onDate} (Default: current date)
                      </span>
                      <input
                        className="form-control"
                        type="date"
                        name="on_date" // Changed name to on_date
                        value={form.on_date}
                        onChange={handleInputField}
                        // disabled={isSubmitted}
                      />
                      {formValid.on_date && (
                        <span className="text-danger">{formValid.on_date}</span>
                      )}
                    </div>
                    <div className="col-md-4">
                      <span className="cardText">{strings.propertyName}</span>
                      <Select
                        className="basic-single"
                        classNamePrefix="select"
                        isSearchable
                        isClearable
                        options={propertyList.map((property) => ({
                          value: property.property_title,
                          label: property.property_title,
                        }))}
                        value={form.property_title}
                        onChange={handleSelectChange} // Handle property selection
                        placeholder="Select property"
                        // isDisabled={isSubmitted} // Disable field after submission
                      />
                    </div>
                    <div className="col-md-4 pt-4">
                      <div className="form-group d-flex justify-content-end">
                        {/* Conditionally render Submit button based on isSubmitted */}
                        {/* {!isSubmitted && ( */}
                        <button
                          className="btn btn-warning mr-4"
                          type="button"
                          onClick={filterDetail}
                        >
                          {strings.submit}
                        </button>
                        {/* )} */}

                        <button
                          className="btn btn-dark"
                          type="button"
                          onClick={filterReset}
                        >
                          {strings.reset}
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
            <hr />

            {/* Bookings Section */}
            {(!isSubmitted || !form.property_title) && (
              <div className="card">
                <div className="card-body">
                  <h3 className="mb-2">{strings.allProjects}</h3>
                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        <th className="col-4 "></th>
                        <th className="col-2 text-right">{strings.ftd}</th>
                        <th className="col-2 text-right">{strings.mtd}</th>
                        <th className="col-2 text-right">{strings.Ytd}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Bookings Section */}
                      <tr>
                        <th colSpan="4" className="table-heading">
                          {strings.bookings}
                        </th>
                      </tr>
                      <tr>
                        <td>Gross Bookings</td>
                        <td className="text-right">
                          {bookings.ftd?.GrossBookings || 0}
                        </td>
                        <td className="text-right">
                          {bookings.mtd?.GrossBookings || 0}
                        </td>
                        <td className="text-right">
                          {bookings.ytd?.GrossBookings || 0}
                        </td>
                      </tr>
                      <tr>
                        <td>Cancellations</td>
                        <td className="text-right">
                          {bookings.ftd?.Cancellation || 0}
                        </td>
                        <td className="text-right">
                          {bookings.mtd?.Cancellation || 0}
                        </td>
                        <td className="text-right">
                          {bookings.ytd?.Cancellation || 0}
                        </td>
                      </tr>
                      <tr>
                        <td>Net Bookings</td>
                        <td className="text-right">
                          {bookings.ftd?.NetBookings || 0}
                        </td>
                        <td className="text-right">
                          {bookings.mtd?.NetBookings || 0}
                        </td>
                        <td className="text-right">
                          {bookings.ytd?.NetBookings || 0}
                        </td>
                      </tr>

                      {/* Site Visits Section */}
                      <tr>
                        <th colSpan="4" className="table-heading">
                          {strings.siteVisits}
                        </th>
                      </tr>
                      <tr>
                        <td>Total Site Visits</td>
                        <td className="text-right">
                          {siteVisits.ftd?.SiteVisits || 0}
                        </td>
                        <td className="text-right">
                          {siteVisits.mtd?.SiteVisits || 0}
                        </td>
                        <td className="text-right">
                          {siteVisits.ytd?.SiteVisits || 0}
                        </td>
                      </tr>
                      <tr>
                        <td>Channel Partner Visits</td>
                        <td className="text-right">
                          {siteVisits.ftd?.ChannelPartnerVisits || 0}
                        </td>
                        <td className="text-right">
                          {siteVisits.mtd?.ChannelPartnerVisits || 0}
                        </td>
                        <td className="text-right">
                          {siteVisits.ytd?.ChannelPartnerVisits || 0}
                        </td>
                      </tr>
                      <tr>
                        <td>Direct Walk-In Visits</td>
                        <td className="text-right">
                          {siteVisits.ftd?.DirectWalkInVisits || 0}
                        </td>
                        <td className="text-right">
                          {siteVisits.mtd?.DirectWalkInVisits || 0}
                        </td>
                        <td className="text-right">
                          {siteVisits.ytd?.DirectWalkInVisits || 0}
                        </td>
                      </tr>
                      <tr>
                        <td>Referral Partner Visits</td>
                        <td className="text-right">
                          {siteVisits.ftd?.ReferralPartnerVisits || 0}
                        </td>
                        <td className="text-right">
                          {siteVisits.mtd?.ReferralPartnerVisits || 0}
                        </td>
                        <td className="text-right">
                          {siteVisits.ytd?.ReferralPartnerVisits || 0}
                        </td>
                      </tr>
                      <tr>
                        <td>Reference Visits</td>
                        <td className="text-right">
                          {siteVisits.ftd?.ReferenceVisits || 0}
                        </td>
                        <td className="text-right">
                          {siteVisits.mtd?.ReferenceVisits || 0}
                        </td>
                        <td className="text-right">
                          {siteVisits.ytd?.ReferenceVisits || 0}
                        </td>
                      </tr>
                      <tr>
                        <td>Pre-Sales Visits</td>
                        <td className="text-right">
                          {siteVisits.ftd?.PreSalesVisits || 0}
                        </td>
                        <td className="text-right">
                          {siteVisits.mtd?.PreSalesVisits || 0}
                        </td>
                        <td className="text-right">
                          {siteVisits.ytd?.PreSalesVisits || 0}
                        </td>
                      </tr>
                      <tr>
                        <td>Exhibition Visits</td>
                        <td className="text-right">
                          {siteVisits.ftd?.ExhibitionVisits || 0}
                        </td>
                        <td className="text-right">
                          {siteVisits.mtd?.ExhibitionVisits || 0}
                        </td>
                        <td className="text-right">
                          {siteVisits.ytd?.ExhibitionVisits || 0}
                        </td>
                      </tr>
                      <tr>
                        <td>Other Visits</td>
                        <td className="text-right">
                          {siteVisits.ftd?.OtherVisits || 0}
                        </td>
                        <td className="text-right">
                          {siteVisits.mtd?.OtherVisits || 0}
                        </td>
                        <td className="text-right">
                          {siteVisits.ytd?.OtherVisits || 0}
                        </td>
                      </tr>
                      <tr>
                        <th colSpan="4" className="table-heading">
                          {strings.reVisits}
                        </th>
                      </tr>
                      <tr>
                        <td>Total Revisits</td>
                        <td className="text-right">
                          {siteVisits.ftd?.Revisits || 0}
                        </td>
                        <td className="text-right">
                          {siteVisits.mtd?.Revisits || 0}
                        </td>
                        <td className="text-right">
                          {siteVisits.ytd?.Revisits || 0}
                        </td>
                      </tr>
                      <tr>
                        <td>ChannelPartner Revisits</td>
                        <td className="text-right">
                          {siteVisits.ftd?.ChannelPartnerRevisits || 0}
                        </td>
                        <td className="text-right">
                          {siteVisits.mtd?.ChannelPartnerRevisits || 0}
                        </td>
                        <td className="text-right">
                          {siteVisits.ytd?.ChannelPartnerRevisits || 0}
                        </td>
                      </tr>
                      <tr>
                        <td>Direct Walk-In Revisits</td>
                        <td className="text-right">
                          {siteVisits.ftd?.DirectWalkInRevisits || 0}
                        </td>
                        <td className="text-right">
                          {siteVisits.mtd?.DirectWalkInRevisits || 0}
                        </td>
                        <td className="text-right">
                          {siteVisits.ytd?.DirectWalkInRevisits || 0}
                        </td>
                      </tr>
                      <tr>
                        <td>Referral Partner Revisits</td>
                        <td className="text-right">
                          {siteVisits.ftd?.ReferralPartnerRevisits || 0}
                        </td>
                        <td className="text-right">
                          {siteVisits.mtd?.ReferralPartnerRevisits || 0}
                        </td>
                        <td className="text-right">
                          {siteVisits.ytd?.ReferralPartnerRevisits || 0}
                        </td>
                      </tr>
                      <tr>
                        <td>Reference Revisits</td>
                        <td className="text-right">
                          {siteVisits.ftd?.ReferenceRevisits || 0}
                        </td>
                        <td className="text-right">
                          {siteVisits.mtd?.ReferenceRevisits || 0}
                        </td>
                        <td className="text-right">
                          {siteVisits.ytd?.ReferenceRevisits || 0}
                        </td>
                      </tr>
                      <tr>
                        <td>Pre-Sales Revisits</td>
                        <td className="text-right">
                          {siteVisits.ftd?.PreSalesRevisits || 0}
                        </td>
                        <td className="text-right">
                          {siteVisits.mtd?.PreSalesRevisits || 0}
                        </td>
                        <td className="text-right">
                          {siteVisits.ytd?.PreSalesRevisits || 0}
                        </td>
                      </tr>
                      <tr>
                        <td>Exhibition Revisits</td>
                        <td className="text-right">
                          {siteVisits.ftd?.ExhibitionRevisits || 0}
                        </td>
                        <td className="text-right">
                          {siteVisits.mtd?.ExhibitionRevisits || 0}
                        </td>
                        <td className="text-right">
                          {siteVisits.ytd?.ExhibitionRevisits || 0}
                        </td>
                      </tr>
                      <tr>
                        <td>Other Revisits</td>
                        <td className="text-right">
                          {siteVisits.ftd?.OtherRevisits || 0}
                        </td>
                        <td className="text-right">
                          {siteVisits.mtd?.OtherRevisits || 0}
                        </td>
                        <td className="text-right">
                          {siteVisits.ytd?.OtherRevisits || 0}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Export Button */}

            <div className="d-flex justify-content-end mb-4">
              <button className="btn btn-primary" onClick={exportToExcel}>
                Export to Excel
              </button>
            </div>

            <div className="mt-4">
              <div className="">
                {propertyList.length > 0 ? (
                  propertyList
                    .sort((a, b) =>
                      a.property_title.localeCompare(b.property_title)
                    )
                    .map((property) => {
                      const isSelected =
                        isSubmitted && form.property_title
                          ? property.property_title ===
                            form.property_title.value
                          : true;
                      if (!isSelected) return null;

                      const ftdBookingData = bookings1[0]?.ftd.find(
                        (b) => b.property_title === property.property_title
                      ) || {
                        GrossBookings: 0,
                        Cancellation: 0,
                        NetBookings: 0,
                        Token: 0,
                      };
                      const mtdBookingData = bookings1[0]?.mtd.find(
                        (b) => b.property_title === property.property_title
                      ) || {
                        GrossBookings: 0,
                        Cancellation: 0,
                        NetBookings: 0,
                        Token: 0,
                      };
                      const ytdBookingData = bookings1[0]?.ytd.find(
                        (b) => b.property_title === property.property_title
                      ) || {
                        GrossBookings: 0,
                        Cancellation: 0,
                        NetBookings: 0,
                        Token: 0,
                      };

                      const ftdSiteVisitData = siteVisits1[0]?.ftd.find(
                        (s) => s.property_title === property.property_title
                      ) || {
                        SiteVisits: 0,
                        Revisits: 0,
                        DirectWalkInVisits: 0,
                        ReferenceVisits: 0,
                        ReferralPartnerVisits: 0,
                        ChannelPartnerVisits: 0,
                        PreSalesVisits: 0,
                        ExhibitionVisits: 0,
                        ChannelPartnerRevisitsCount: 0,
                        DirectWalkInRevisitsCount: 0,
                        ReferenceRevisitsCount: 0,
                        ReferralPartnerRevisitsCount: 0,
                        PreSalesRevisitsCount: 0,
                        ExhibitionRevisitsCount: 0,
                        OtherVisitsCount: 0,
                        OtherRevisitsCount: 0,
                      };
                      const mtdSiteVisitData = siteVisits1[0]?.mtd.find(
                        (s) => s.property_title === property.property_title
                      ) || {
                        SiteVisits: 0,
                        Revisits: 0,
                        DirectWalkInVisits: 0,
                        ReferenceVisits: 0,
                        ReferralPartnerVisits: 0,
                        ChannelPartnerVisits: 0,
                        PreSalesVisits: 0,
                        ExhibitionVisits: 0,
                        ChannelPartnerRevisitsCount: 0,
                        DirectWalkInRevisitsCount: 0,
                        ReferenceRevisitsCount: 0,
                        ReferralPartnerRevisitsCount: 0,
                        PreSalesRevisitsCount: 0,
                        ExhibitionRevisitsCount: 0,
                        OtherVisitsCount: 0,
                        OtherRevisitsCount: 0,
                      };
                      const ytdSiteVisitData = siteVisits1[0]?.ytd.find(
                        (s) => s.property_title === property.property_title
                      ) || {
                        SiteVisits: 0,
                        Revisits: 0,
                        DirectWalkInVisits: 0,
                        ReferenceVisits: 0,
                        ReferralPartnerVisits: 0,
                        ChannelPartnerVisits: 0,
                        PreSalesVisits: 0,
                        ExhibitionVisits: 0,
                        ChannelPartnerRevisitsCount: 0,
                        DirectWalkInRevisitsCount: 0,
                        ReferenceRevisitsCount: 0,
                        ReferralPartnerRevisitsCount: 0,
                        PreSalesRevisitsCount: 0,
                        ExhibitionRevisitsCount: 0,
                        OtherVisitsCount: 0,
                        OtherRevisitsCount: 0,
                      };

                      return (
                        <div
                          className="card mt-4"
                          style={{ border: "1px solid #a1a1a1" }}
                          key={property.property_title}
                        >
                          <div className="card-body">
                            <div className="mb-4">
                              <h5>
                                Property Title:{" "}
                                <span
                                  style={{ color: "blue", fontWeight: "bold" }}
                                >
                                  {property.property_title}
                                </span>
                              </h5>

                              {/* FTD, MTD, YTD Labels */}
                              <table className="table table-bordered mb-3">
                                <thead>
                                  <tr>
                                    <th className="col-4"></th>
                                    <th className="col-2 text-right">
                                      {strings.ftd}
                                    </th>
                                    <th className="col-2 text-right">
                                      {strings.mtd}
                                    </th>
                                    <th className="col-2 text-right">
                                      {strings.Ytd}
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {/* Bookings Section */}
                                  <tr>
                                    <th colSpan="4" className="table-heading">
                                      {strings.bookings}
                                    </th>
                                  </tr>
                                  <tr>
                                    <td>Gross Bookings</td>
                                    <td className="text-right">
                                      {ftdBookingData.GrossBookings}
                                    </td>
                                    <td className="text-right">
                                      {mtdBookingData.GrossBookings}
                                    </td>
                                    <td className="text-right">
                                      {ytdBookingData.GrossBookings}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Cancellations</td>
                                    <td className="text-right">
                                      {ftdBookingData.Cancellation}
                                    </td>
                                    <td className="text-right">
                                      {mtdBookingData.Cancellation}
                                    </td>
                                    <td className="text-right">
                                      {ytdBookingData.Cancellation}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Net Bookings</td>
                                    <td className="text-right">
                                      {ftdBookingData.NetBookings}
                                    </td>
                                    <td className="text-right">
                                      {mtdBookingData.NetBookings}
                                    </td>
                                    <td className="text-right">
                                      {ytdBookingData.NetBookings}
                                    </td>
                                  </tr>

                                  {/* Site Visits Section */}
                                  <tr>
                                    <th colSpan="4" className="table-heading">
                                      {strings.siteVisits}
                                    </th>
                                  </tr>
                                  <tr>
                                    <td>Total Site Visits</td>
                                    <td className="text-right">
                                      {ftdSiteVisitData.SiteVisits || 0}
                                    </td>
                                    <td className="text-right">
                                      {mtdSiteVisitData.SiteVisits || 0}
                                    </td>
                                    <td className="text-right">
                                      {ytdSiteVisitData.SiteVisits || 0}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Channel Partner Visits</td>
                                    <td className="text-right">
                                      {ftdSiteVisitData.ChannelPartnerVisits ||
                                        0}
                                    </td>
                                    <td className="text-right">
                                      {mtdSiteVisitData.ChannelPartnerVisits ||
                                        0}
                                    </td>
                                    <td className="text-right">
                                      {ytdSiteVisitData.ChannelPartnerVisits ||
                                        0}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Direct Walk-In Visits</td>
                                    <td className="text-right">
                                      {ftdSiteVisitData.DirectWalkInVisits || 0}
                                    </td>
                                    <td className="text-right">
                                      {mtdSiteVisitData.DirectWalkInVisits || 0}
                                    </td>
                                    <td className="text-right">
                                      {ytdSiteVisitData.DirectWalkInVisits || 0}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Referral Partner Visits</td>
                                    <td className="text-right">
                                      {ftdSiteVisitData.ReferralPartnerVisits ||
                                        0}
                                    </td>
                                    <td className="text-right">
                                      {mtdSiteVisitData.ReferralPartnerVisits ||
                                        0}
                                    </td>
                                    <td className="text-right">
                                      {ytdSiteVisitData.ReferralPartnerVisits ||
                                        0}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Reference Visits</td>
                                    <td className="text-right">
                                      {ftdSiteVisitData.ReferenceVisits || 0}
                                    </td>
                                    <td className="text-right">
                                      {mtdSiteVisitData.ReferenceVisits || 0}
                                    </td>
                                    <td className="text-right">
                                      {ytdSiteVisitData.ReferenceVisits || 0}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Pre-Sales Visits</td>
                                    <td className="text-right">
                                      {ftdSiteVisitData.PreSalesVisits || 0}
                                    </td>
                                    <td className="text-right">
                                      {mtdSiteVisitData.PreSalesVisits || 0}
                                    </td>
                                    <td className="text-right">
                                      {ytdSiteVisitData.PreSalesVisits || 0}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Exhibition Visits</td>
                                    <td className="text-right">
                                      {ftdSiteVisitData.ExhibitionVisits || 0}
                                    </td>
                                    <td className="text-right">
                                      {mtdSiteVisitData.ExhibitionVisits || 0}
                                    </td>
                                    <td className="text-right">
                                      {ytdSiteVisitData.ExhibitionVisits || 0}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Other Visits</td>
                                    <td className="text-right">
                                      {ftdSiteVisitData.OtherVisitsCount || 0}
                                    </td>
                                    <td className="text-right">
                                      {mtdSiteVisitData.OtherVisitsCount || 0}
                                    </td>
                                    <td className="text-right">
                                      {ytdSiteVisitData.OtherVisitsCount || 0}
                                    </td>
                                  </tr>
                                  <tr>
                                    <th colSpan="4" className="table-heading">
                                      {strings.reVisits}
                                    </th>
                                  </tr>
                                  <tr>
                                    <td>Total Revisits</td>
                                    <td className="text-right">
                                      {ftdSiteVisitData.Revisits || 0}
                                    </td>
                                    <td className="text-right">
                                      {mtdSiteVisitData.Revisits || 0}
                                    </td>
                                    <td className="text-right">
                                      {ytdSiteVisitData.Revisits || 0}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>ChannelPartner Revisits</td>
                                    <td className="text-right">
                                      {ftdSiteVisitData.ChannelPartnerRevisitsCount ||
                                        0}
                                    </td>
                                    <td className="text-right">
                                      {mtdSiteVisitData.ChannelPartnerRevisitsCount ||
                                        0}
                                    </td>
                                    <td className="text-right">
                                      {ytdSiteVisitData.ChannelPartnerRevisitsCount ||
                                        0}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Direct Walk-In Revisits</td>
                                    <td className="text-right">
                                      {ftdSiteVisitData.DirectWalkInRevisitsCount ||
                                        0}
                                    </td>
                                    <td className="text-right">
                                      {mtdSiteVisitData.DirectWalkInRevisitsCount ||
                                        0}
                                    </td>
                                    <td className="text-right">
                                      {ytdSiteVisitData.DirectWalkInRevisitsCount ||
                                        0}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Referral Partner Revisits</td>
                                    <td className="text-right">
                                      {ftdSiteVisitData.ReferralPartnerRevisitsCount ||
                                        0}
                                    </td>
                                    <td className="text-right">
                                      {mtdSiteVisitData.ReferralPartnerRevisitsCount ||
                                        0}
                                    </td>
                                    <td className="text-right">
                                      {ytdSiteVisitData.ReferralPartnerRevisitsCount ||
                                        0}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Reference Revisits</td>
                                    <td className="text-right">
                                      {ftdSiteVisitData.ReferenceRevisitsCount ||
                                        0}
                                    </td>
                                    <td className="text-right">
                                      {mtdSiteVisitData.ReferenceRevisitsCount ||
                                        0}
                                    </td>
                                    <td className="text-right">
                                      {ytdSiteVisitData.ReferenceRevisitsCount ||
                                        0}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Pre-Sales Revisits</td>
                                    <td className="text-right">
                                      {ftdSiteVisitData.PreSalesRevisitsCount ||
                                        0}
                                    </td>
                                    <td className="text-right">
                                      {mtdSiteVisitData.PreSalesRevisitsCount ||
                                        0}
                                    </td>
                                    <td className="text-right">
                                      {ytdSiteVisitData.PreSalesRevisitsCount ||
                                        0}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Exhibition Revisits</td>
                                    <td className="text-right">
                                      {ftdSiteVisitData.ExhibitionRevisitsCount ||
                                        0}
                                    </td>
                                    <td className="text-right">
                                      {mtdSiteVisitData.ExhibitionRevisitsCount ||
                                        0}
                                    </td>
                                    <td className="text-right">
                                      {ytdSiteVisitData.ExhibitionRevisitsCount ||
                                        0}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Other Revisits</td>
                                    <td className="text-right">
                                      {ftdSiteVisitData.OtherRevisitsCount || 0}
                                    </td>
                                    <td className="text-right">
                                      {mtdSiteVisitData.OtherRevisitsCount || 0}
                                    </td>
                                    <td className="text-right">
                                      {ytdSiteVisitData.OtherRevisitsCount || 0}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      );
                    })
                ) : (
                  <TableDataNotFound message="No properties found." />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetailsReportPage;
